import { Heading } from "common/components/Heading"
import { useHasBeenInViewport } from "common/hooks/interaction/useHasBeenInViewport"
import { isValidUrl } from "common/lib/isValidUrl"
import { isYouTubeUrl, normalizeUrl, thumbnailUrl } from "common/lib/youtube"
import { useTranslations } from "modules/i18n/hooks/useTranslations"
import { useEffect } from "react"
import { VideoTrackingEventType, trackVideoEvent } from "modules/promos/lib/tracking"
import { trackVideoEngaged, trackVideoViewed } from "common/lib/tcEventCollector"
import { useTrackLinkClick } from "common/hooks/interaction/useTrackLinkClick"

interface VideoWithMetadataProps {
  src: string
  heading: string
  body: string
  linkText: string
  href: string
  analyticsId: string
  disable?: boolean
}

export const VideoWithMetadata = ({
  src,
  heading,
  body,
  linkText,
  href,
  analyticsId,
  disable,
}: VideoWithMetadataProps) => {
  const t = useTranslations("blocks.video")
  const { setRef, hasBeenInViewport } = useHasBeenInViewport(100)
  const sanitizedHref = href && isValidUrl(href) ? href : undefined

  useEffect(() => {
    if (analyticsId && analyticsId.length > 0 && hasBeenInViewport) {
      // Emit GA4 tracking event
      trackVideoEvent(VideoTrackingEventType.Impression, analyticsId)
      // Emit in-house tracking event
      trackVideoViewed(analyticsId)
    }
  }, [hasBeenInViewport, analyticsId])

  const trackClick = useTrackLinkClick(href, analyticsId, () => {
    // Emit GA4 tracking event
    trackVideoEvent(VideoTrackingEventType.Engagement, analyticsId)
    // Emit in-house tracking event
    trackVideoEngaged(analyticsId)
  })
  const youtubeVideo = disable ? (
    <img src={thumbnailUrl(src, "hq")} alt={t("youtube_thumbnail_label")} className="w-full" />
  ) : (
    <iframe
      className="aspect-video w-full"
      src={normalizeUrl(src)}
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      title={t("youtube_label")}
    />
  )

  return (
    <figure
      className="my-4 grid max-w-(--breakpoint-xl) grid-cols-1 gap-0 sm:grid-cols-2 sm:items-center sm:gap-x-12 lg:my-9"
      ref={setRef}
      data-testid={isYouTubeUrl(src) ? "youtube-figure" : undefined}
    >
      <div className="order-1 sm:col-start-1 sm:row-start-1">
        {isYouTubeUrl(src) ? (
          <div className="overflow-hidden rounded-xl bg-black">
            {hasBeenInViewport && youtubeVideo}
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl">
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              src={src}
              data-testid="video-player"
              controls
              className="w-full"
              aria-label={t("generic_label")}
            />
          </div>
        )}
      </div>
      <div className="order-3 flex flex-col justify-center gap-2 py-4 sm:col-start-2 sm:row-start-1 sm:gap-4 sm:pt-0">
        {heading && (
          <Heading level={3} styledAsLevel={3}>
            {heading}
          </Heading>
        )}
        {body && <p className="mb-2 font-serif whitespace-pre-line lg:text-lg">{body}</p>}
        {linkText && sanitizedHref && (
          <a
            href={sanitizedHref}
            className="self-start rounded-full bg-gray-50 px-5 py-3 font-semibold text-black hover:bg-gray-100"
            onClick={trackClick}
            onAuxClick={trackClick}
          >
            {linkText}
          </a>
        )}
      </div>
    </figure>
  )
}
